import type { ISite } from "@similarweb/ui-components";
import { ListItemSeparator } from "@similarweb/ui-components";

import type { Recent } from "app/custom-hooks/useRecentsHook";
import { translate } from "app/filters/i18nFilter";
import { fetchWebsitesFaviconsQuery } from "app/services/sitesService/queries/sitesQueries";
import {
    generateRecents,
    getWebsiteResults,
    getKeywordResults,
    createMixedSearchResults,
} from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";

export const defaultWebsiteState = "digitalsuite_website_websiteperformance";
export const defaultKeywordState = "organicsearch_keywordAnalysis_overview_2";
const defaultListItemSeparatorTextKey = "autocomplete.digital.marketing.list.item.separator";

export const BLOCK_SITE_STATUS = 2;

export const generateAutocompleteRecentItems = (
    recentWebsites: Recent[],
    recentKeywords: FixmeType,
    websiteState = defaultWebsiteState,
    maxCompareItemsToDisplay?: number,
    onItemClick?: (key: string) => void,
    shouldNavigate?: (key: string) => boolean,
    RecentListSeparatorTextKey = defaultListItemSeparatorTextKey,
) => {
    return generateRecents(
        [...recentWebsites, ...recentKeywords],
        { website: websiteState, keyword: defaultKeywordState },
        <ListItemSeparator key="top-separator">{translate(RecentListSeparatorTextKey)}</ListItemSeparator>,
        undefined,
        maxCompareItemsToDisplay,
        onItemClick,
        shouldNavigate,
    );
};
export const generateAutocompleteDefaultItems = async (
    websiteState: string,
    keywordState: string | undefined,
    popularSitesExamples = ["facebook.com", "amazon.com", "youtube.com"],
    onItemClick?: (key: string) => void,
    shouldNavigate?: (key: string) => boolean,
) => {
    const websitesWithFavicons = await fetchWebsitesFaviconsQuery(popularSitesExamples);
    const defaultItemsWebsites = Object.entries(websitesWithFavicons).map(([key, value]) => ({
        name: key,
        image: value,
        type: "website",
    }));
    const defaultItemsKeyword = [
        { name: "travel", type: "keyword" },
        { name: "tiktok", type: "keyword" },
    ];
    return [
        <ListItemSeparator key="default_items_title">
            {translate("autocomplete.digital.trial.list.item.default")}
        </ListItemSeparator>,
        createMixedSearchResults(
            [...(websiteState ? defaultItemsWebsites : []), ...(keywordState ? defaultItemsKeyword : [])],
            // @ts-ignore FIXME: strict migration - Argument of type 'null' is not assignable to parameter of type 'string' — no app-type items in digital marketing autocomplete, appPageStateName is never consumed
            null,
            websiteState,
            keywordState,
            onItemClick,
            shouldNavigate,
        ),
    ];
};
const mergeAndSortSearchResults = (
    websiteResults: ISite[],
    keywordResults?: ISite[],
): Array<ISite & { type: string }> => {
    const websiteWithType = websiteResults.map((item) => ({ ...item, type: "website" }));
    const keywordWithType = keywordResults?.map((item) => ({ ...item, type: "keyword" })) ?? [];
    const topWebsites = websiteWithType.splice(0, 5);
    const otherResults = [...websiteWithType, ...keywordWithType].sort((a, b) => {
        const aVal = a.name.toUpperCase();
        const bVal = b.name.toUpperCase();
        if (aVal < bVal) {
            return -1;
        }

        if (aVal > bVal) {
            return 1;
        }

        return 0;
    });
    return [...topWebsites, ...otherResults];
};

export const generateAutocompleteSearchResultItems = async (
    query: string,
    includeKeywordResults: boolean,
    websiteState = defaultWebsiteState,
    onItemClick?: (key: string) => void,
    shouldNavigate?: (key: string) => boolean,
) => {
    const websiteResults = await getWebsiteResults(query, includeKeywordResults ? 9 : 30);
    const keywordResults = includeKeywordResults ? await getKeywordResults(query) : [];
    const filterBlockSites = websiteResults.filter((res) =>
        Array.isArray(res.blockStatus) ? !res.blockStatus.includes(BLOCK_SITE_STATUS) : true,
    );

    return createMixedSearchResults(
        mergeAndSortSearchResults(filterBlockSites, keywordResults),
        // @ts-ignore FIXME: strict migration - Argument of type 'null' is not assignable to parameter of type 'string' — no app-type items in digital marketing autocomplete, appPageStateName is never consumed
        null,
        websiteState,
        defaultKeywordState,
        onItemClick,
        shouldNavigate,
    );
};
